import { JSX, useContext, useEffect, useState } from "react";
import { ProviderNetworkType } from "@app/projects/enumerators";
import { ClaimsActivityProviderSummary, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { getRoundingDisplay } from "@app/projects/network-assessment/pci-dashboard/shared/utility";
import { PciDashboardFiltersContext } from "@app/projects/network-assessment/pci-dashboard/shared";
import { formatDateTime, TooltipWrapper } from "@app/shared";
import { DashboardTile, QESButton } from "@app/shared/qes-standards";
import { capitalizeFirstLetter } from "@app/shared/utility";
import { ColumnConfig, Waffles, printNumberAsString, wafflesKeyColumnName } from "@app/shared/waffles";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";

interface ClaimsActivityProvidersTileProps {
  data: ClaimsActivityProviderSummary[];
  loading: boolean;
  onDashboardDetailsViewClick: (claimsLabel: ClaimsActivityVolumeLabelEnum, providerNetworkType: ProviderNetworkType) => void;
  providerType: ProviderNetworkType;
}

interface ClaimsActivityProviderTableData {
  npiCount: string | JSX.Element;
  npiPercentage: string;
  type: string | JSX.Element;
  [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
  npiCount: {
    alignment: TextAlignment.Right,
    displayText: "Providers",
    noSort: true,
    testId: "npi-count",
  },
  npiPercentage: {
    alignment: TextAlignment.Right,
    displayText: "% of Providers",
    noSort: true,
    testId: "npi-percentage",
  },
  type: {
    alignment: TextAlignment.Left,
    displayText: "",
    noSort: true,
    testId: "type",
  }
};

const ClaimsActivityProvidersTile = (props: ClaimsActivityProvidersTileProps): JSX.Element => {
  const [tableData, setTableData] = useState<ClaimsActivityProviderTableData[]>([]);

  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);

  useEffect(() => {
    if (props.data) {
      /* eslint-disable sort-keys */
      const newTableData: ClaimsActivityProviderTableData[] = props.data.map((summary, index) => {
        return {
          [wafflesKeyColumnName]: summary.claimsVolumeLabel || index.toString(),
          type: summary.claimsVolumeLabel ? <TooltipWrapper text={getClaimsVolumeToolTip(summary.claimsVolumeLabel)}>{summary.claimsVolumeLabel}</TooltipWrapper> : "---",
            npiCount: (summary.claimsVolumeLabel !== ClaimsActivityVolumeLabelEnum.Total && !!summary.servicingCount) ? (
            <QESButton
              onClick={() => props.onDashboardDetailsViewClick(summary.claimsVolumeLabel, props.providerType)}
              qesTestId={`claims-activity__providers-tile--dashboard-details-${props.providerType}-${summary.claimsVolumeLabel.toLowerCase()}`}
              qesType="secondary"
            >
              {printNumberAsString(summary.servicingCount, "0")}
            </QESButton>
          ) : printNumberAsString(summary.servicingCount, "0"),
          npiPercentage: getRoundingDisplay(summary.percentage, true),
        }
      });
      /* eslint-enable sort-keys */
      setTableData(newTableData);
    }
  }, [props.data, pciDashboardFiltersContext.displayedFilters]);

  const getClaimsVolumeToolTip = (claimsVolumeLabel: ClaimsActivityVolumeLabelEnum): string => {
    let claimsVolumeToolTip: string;
    switch (claimsVolumeLabel) {
      case ClaimsActivityVolumeLabelEnum.Core:
        claimsVolumeToolTip = ">75th percentile in patient volume"
        break;
      case ClaimsActivityVolumeLabelEnum.Standard:
        claimsVolumeToolTip = "25th-75th percentile in patient volume"
        break;
      case ClaimsActivityVolumeLabelEnum.Peripheral:
        claimsVolumeToolTip = "<25th percentile in patient volume"
        break;
      case ClaimsActivityVolumeLabelEnum.Ghost:
        claimsVolumeToolTip = "No claims"
        break;
      case ClaimsActivityVolumeLabelEnum.RxOnly:
        claimsVolumeToolTip = "Claims in which only Prescriptions are present"
        break;
      case ClaimsActivityVolumeLabelEnum.InsufficientData:
        claimsVolumeToolTip = "No claims activity but limited data"
        break;
    }
    return claimsVolumeToolTip;
  }

  const rowDecorator = (row: ClaimsActivityProviderTableData) => {
    if (row.type === "Total") {
      return "footer";
    }
  }

  return (
    <DashboardTile
      classNames="pci-dashboard__provider-tile"
      loading={props.loading}
      showTitleSeparator={true}
      subtitle={`As of ${formatDateTime(pciDashboardFiltersContext.projectRunConfiguration?.dateCreated)}`}
      title={capitalizeFirstLetter(props.providerType)}
      widthClass="col-md-4"
    >
      <Waffles<ClaimsActivityProviderTableData>
        columns={columnConfig}
        data={tableData}
        disableStickyHeader={true}
        fullWidth={true}
        hideTableHead={false}
        keyColumns={["type"]}
        rowDecorator={rowDecorator}
        testId={`claims-activity__providers-tile--${props.providerType}`}
        theme={WafflesThemes.Simple}
      />
    </DashboardTile>
  )
}

export default ClaimsActivityProvidersTile;